import {
  KnockGuideProvider,
  KnockProvider,
  useKnockClient,
} from '@knocklabs/react';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { type Me, useGetMeApi } from 'eds-common/api/v2';
import { KNOCK_API_KEY, KNOCK_GUIDE_CHANNEL_ID } from '@/env-variables';
import { useClientEnvs } from '@/hooks/api/use-client-envs/use-client-envs';
import { useCustomJwtQuery } from '@/hooks/api/use-custom-jwt/use-custom-jwt';

function KnockGuideSetup({
  user,
  children,
}: PropsWithChildren<{ user: Me | null | undefined }>) {
  const knock = useKnockClient();
  const { data: clientEnvsMock } = useClientEnvs();
  const [isIdentified, setIsIdentified] = useState(false);

  useEffect(() => {
    if (!knock.userId) {
      return;
    }

    knock.user
      .identify()
      .catch((err: unknown) =>
        console.error('Could not identify user to Knock', err)
      )
      .finally(() => setIsIdentified(true));
  }, [knock.userId, knock.user]);

  return (
    <KnockGuideProvider
      channelId={clientEnvsMock.knockGuideChannelId ?? KNOCK_GUIDE_CHANNEL_ID}
      readyToTarget={isIdentified}
      listenForUpdates
      targetParams={{
        data: {
          currentRole: user?.currentRole,
          userSource: user?.userSource,
          otherSource: user?.otherSource?.toLowerCase(),
        },
      }}
    >
      {children}
    </KnockGuideProvider>
  );
}

export function AppProvider({ children }: PropsWithChildren) {
  const { data: user } = useGetMeApi();
  const { data: clientEnvsMock } = useClientEnvs();
  const { data: knockJwt, refetch } = useCustomJwtQuery('KNOCK');

  return (
    <KnockProvider
      apiKey={clientEnvsMock.knockApiKey ?? KNOCK_API_KEY}
      userId={user?.userId}
      userToken={knockJwt?.token}
      onUserTokenExpiring={async () => {
        await refetch();
      }}
    >
      <KnockGuideSetup user={user}>{children}</KnockGuideSetup>
    </KnockProvider>
  );
}
